import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, ArrowLeft, Luggage } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { MultiCitySelector, CityStop } from './MultiCitySelector';
import { MultiCityPackingList } from './MultiCityPackingList';
import { PackingLoadingAnimation } from './PackingLoadingAnimation';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface MultiCityPackingWorkflowProps {
  onComplete: () => void;
}

export function MultiCityPackingWorkflow({ onComplete }: MultiCityPackingWorkflowProps) {
  const [currentStep, setCurrentStep] = useState<'form' | 'loading' | 'packing'>('form');
  const [cities, setCities] = useState<CityStop[]>([]);
  const [tripStartDate, setTripStartDate] = useState<Date>();
  const [tripEndDate, setTripEndDate] = useState<Date>();
  const [tripType, setTripType] = useState('');
  const [packingData, setPackingData] = useState<Record<string, any>>({});
  const { user } = useAuth();
  const { toast } = useToast();

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (cities.length === 0) {
      toast({
        title: "Add destinations",
        description: "Please add at least one city to your trip",
        variant: "destructive"
      });
      return;
    }

    if (!tripStartDate || !tripEndDate) {
      toast({
        title: "Set dates",
        description: "Please set your trip start and end dates",
        variant: "destructive"
      });
      return;
    }

    setCurrentStep('loading');
  };

  const handleLoadingComplete = async () => {
    // Generate packing data for each city
    const data: Record<string, any> = {};
    
    for (const city of cities) {
      // Generate mock packing data per city
      data[city.id] = {
        destination: city.destination,
        country: city.country,
        weather: {
          condition: 'Partly cloudy',
          temp: '22°C',
        },
        categories: [
          {
            name: 'Clothing',
            items: [
              { name: 'T-shirts', quantity: 3, packed: false },
              { name: 'Pants/Jeans', quantity: 2, packed: false },
              { name: 'Underwear', quantity: 4, packed: false },
              { name: 'Socks', quantity: 4, packed: false },
              { name: 'Light jacket', quantity: 1, packed: false },
            ]
          },
          {
            name: 'Toiletries',
            items: [
              { name: 'Toothbrush & toothpaste', quantity: 1, packed: false },
              { name: 'Shampoo & soap', quantity: 1, packed: false },
              { name: 'Sunscreen', quantity: 1, packed: false },
            ]
          },
          {
            name: 'Electronics',
            items: [
              { name: 'Phone charger', quantity: 1, packed: false },
              { name: 'Power adapter', quantity: 1, packed: false },
              { name: 'Headphones', quantity: 1, packed: false },
            ]
          },
          {
            name: 'Documents',
            items: [
              { name: 'Passport', quantity: 1, packed: false },
              { name: 'Travel insurance', quantity: 1, packed: false },
              { name: 'Hotel confirmations', quantity: 1, packed: false },
            ]
          }
        ]
      };
    }
    
    setPackingData(data);
    setCurrentStep('packing');
  };

  const handleUpdateProgress = (cityId: string, categoryIndex: number, itemIndex: number, checked: boolean) => {
    setPackingData(prev => {
      const updated = { ...prev };
      if (updated[cityId]?.categories?.[categoryIndex]?.items?.[itemIndex]) {
        updated[cityId].categories[categoryIndex].items[itemIndex].packed = checked;
      }
      return updated;
    });
  };

  if (currentStep === 'loading') {
    return (
      <PackingLoadingAnimation
        onComplete={handleLoadingComplete}
      />
    );
  }

  if (currentStep === 'packing') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setCurrentStep('form')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Form
          </Button>
          <div>
            <h2 className="text-2xl font-bold">Multi-City Packing List</h2>
            <p className="text-muted-foreground">
              {tripStartDate && tripEndDate && (
                `${format(tripStartDate, 'MMM d')} - ${format(tripEndDate, 'MMM d, yyyy')}`
              )}
            </p>
          </div>
        </div>

        <MultiCityPackingList
          cities={cities}
          packingData={packingData}
          onUpdateProgress={handleUpdateProgress}
        />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onComplete}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6">
      <div className="flex items-center gap-4">
        <Button type="button" variant="ghost" onClick={onComplete}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div>
          <h2 className="text-2xl font-bold">Multi-City Trip Planning</h2>
          <p className="text-muted-foreground">Add your destinations and travel details</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Trip Duration</CardTitle>
          <CardDescription>Set your overall trip dates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Trip Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !tripStartDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {tripStartDate ? format(tripStartDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={tripStartDate}
                    onSelect={setTripStartDate}
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Trip End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !tripEndDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {tripEndDate ? format(tripEndDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={tripEndDate}
                    onSelect={setTripEndDate}
                    disabled={(date) => !tripStartDate || date < tripStartDate}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Trip Type</Label>
            <Select value={tripType} onValueChange={setTripType}>
              <SelectTrigger>
                <SelectValue placeholder="Select trip type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="business">Business Travel</SelectItem>
                <SelectItem value="leisure">Leisure/Vacation</SelectItem>
                <SelectItem value="backpacking">Backpacking</SelectItem>
                <SelectItem value="adventure">Adventure</SelectItem>
                <SelectItem value="cultural">Cultural Tour</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your Destinations</CardTitle>
          <CardDescription>Add cities in the order you'll visit them</CardDescription>
        </CardHeader>
        <CardContent>
          <MultiCitySelector
            cities={cities}
            onCitiesChange={setCities}
            tripStartDate={tripStartDate}
            tripEndDate={tripEndDate}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onComplete}>
          Cancel
        </Button>
        <Button 
          type="submit"
          disabled={cities.length === 0 || !tripStartDate || !tripEndDate || !tripType}
        >
          <Luggage className="mr-2 h-4 w-4" />
          Generate Packing Lists
        </Button>
      </div>
    </form>
  );
}
